/**
 * NaicsCodesPage — Reference Data · NAICS Codes.
 *
 * The IRS / U.S. Census Bureau business-classification catalog (NAICS,
 * 2–8 digit). Business entity types read this list so officers can pick
 * what kind of business they're opening an account for; the chosen code
 * lands on the Business CIF record. A large, mostly-read catalog (~197
 * codes across ~20 two-digit sectors) — the IRS only updates it every
 * few years.
 *
 * Contract (verified against the backend 2026-06-16 — entity, read route,
 * upsert DTO, and service):
 *   - Read:   GET /v3/config/naics
 *             → V3ConfigFunction.Naics returns `{ codes, sectors }`
 *             (NOT a flat array). `codes` = ALL rows (no WHERE IsActive
 *             filter), ordered by Code; `sectors` = the 2-digit sector
 *             catalog. Entity props camelCase 1:1 onto the wire:
 *             code/title/depth/sectorCode/parentCode/isActive (codes),
 *             sectorCode/name/isActive (sectors).
 *
 *             ⚠ DRIFT: the typed read `client.retail.config.naicsCodes()`
 *             is declared `ApiResult<ReadonlyArray<NaicsCode>>` (a flat
 *             array) but the route actually returns the `{ codes, sectors }`
 *             envelope. Using the typed method would lose the sectors and
 *             mis-shape `data`. So this page reads via the raw client with
 *             the correct inline `{ codes, sectors }` type. (Reported for a
 *             follow-up fix to config.ts ConfigNaicsApi.)
 *
 *   - Write:  POST /v3/admin/naics-codes
 *             → AdminReferenceService.UpsertNaicsCodeAsync (EF createOrUpdate,
 *             natural key Code). Service persists EVERY DTO field on both
 *             the create and update branches (no silent drop). Depth is a
 *             server byte? that defaults to code.Length when omitted.
 *
 *   - Delete choice — SOFT delete (re-upsert isActive=false). The hard
 *             DELETE route (admin.reference.naicsCodes.remove) EF-Removes
 *             the row, which would orphan any Business CIF record that
 *             carries this NAICS code. Because the read route does NOT
 *             filter on IsActive, an inactive row stays visible here and
 *             can be flipped back on at any time — so soft delete is both
 *             safe and reversible.
 *
 * Migrated 2026-06-16 from ROP-teams Admin
 * (apps/ROP-teams/src/modules/Admin/editors/naicsCodesEditor.tsx — the
 * operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN_AUTH_TOKEN_SENTINEL, ApiError } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface NaicsCodeRow {
  code: string;
  title: string;
  depth: number;
  sectorCode: string;
  parentCode: string | null;
  isActive: boolean;
}

interface NaicsSectorRow {
  sectorCode: string;
  name: string;
  isActive: boolean;
}

interface EditDraft extends NaicsCodeRow {
  isNew: boolean;
}

function emptyDraft(defaultSector: string): EditDraft {
  return {
    code: '',
    title: '',
    depth: 6,
    sectorCode: defaultSector,
    parentCode: null,
    isActive: true,
    isNew: true,
  };
}

const CODE_PATTERN = /^\d{2,8}$/;

export function NaicsCodesPage(): JSX.Element {
  const client = useApiClient();
  const [codes, setCodes] = useState<ReadonlyArray<NaicsCodeRow> | null>(null);
  const [sectors, setSectors] = useState<ReadonlyArray<NaicsSectorRow>>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NaicsCodeRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setCodes(null);
      setLoadError(null);
      try {
        // Raw read: the typed config.naicsCodes() is mistyped as a flat
        // array, but the route returns a { codes, sectors } envelope.
        const resp = await client.http.get<{
          success: boolean;
          data?: { codes: ReadonlyArray<NaicsCodeRow>; sectors: ReadonlyArray<NaicsSectorRow> };
          error?: string;
        }>('/v3/config/naics');
        if (cancelled) return;
        if (resp.success && resp.data) {
          setCodes(
            resp.data.codes.map((c) => ({
              code: c.code,
              title: c.title,
              depth: c.depth,
              sectorCode: c.sectorCode,
              parentCode: c.parentCode ?? null,
              isActive: c.isActive,
            })),
          );
          setSectors(resp.data.sectors ?? []);
        } else {
          setLoadError(resp.error ?? 'Failed to load NAICS codes.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const sectorByCode = useMemo(() => {
    const m = new Map<string, NaicsSectorRow>();
    for (const s of sectors) m.set(s.sectorCode, s);
    return m;
  }, [sectors]);

  const sectorOptions = useMemo(
    () => [
      { value: '', label: 'All sectors' },
      ...sectors
        .slice()
        .sort((a, b) => a.sectorCode.localeCompare(b.sectorCode))
        .map((s) => ({ value: s.sectorCode, label: `${s.sectorCode} · ${s.name}` })),
    ],
    [sectors],
  );

  const draftSectorOptions = useMemo(
    () => [
      { value: '', label: '— Select sector —' },
      ...sectors
        .slice()
        .sort((a, b) => a.sectorCode.localeCompare(b.sectorCode))
        .map((s) => ({ value: s.sectorCode, label: `${s.sectorCode} · ${s.name}` })),
    ],
    [sectors],
  );

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (codes ?? [])
      .filter((c) => (sectorFilter ? c.sectorCode === sectorFilter : true))
      .filter((c) =>
        needle ? `${c.code} ${c.title}`.toLowerCase().includes(needle) : true,
      )
      .slice()
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [codes, search, sectorFilter]);

  const upsert = useCallback(
    async (d: EditDraft): Promise<boolean> => {
      const resp = await client.retail.admin.reference.naicsCodes.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        {
          code: d.code.trim(),
          title: d.title.trim(),
          depth: d.depth,
          sectorCode: d.sectorCode.trim(),
          parentCode: d.parentCode,
          isActive: d.isActive,
        },
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    const code = draft.code.trim();
    if (!CODE_PATTERN.test(code)) {
      setSaveError('NAICS code must be 2–8 digits.');
      return;
    }
    if (!draft.title.trim()) {
      setSaveError('A title is required.');
      return;
    }
    if (!draft.sectorCode.trim()) {
      setSaveError('Pick a sector for this code.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draft);
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew ? `Created NAICS code ${code}.` : `Updated NAICS code ${code}.`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Soft-disable via re-upsert: Business CIF records reference Code, so
      // a hard delete would orphan them. The read route surfaces inactive
      // rows, so this stays reversible.
      await upsert({ ...deleteTarget, isActive: false, isNew: false });
      setToast({
        tone: 'warn',
        text: `${deleteTarget.code} (${deleteTarget.title}) is now inactive. Officers won't see it in the NAICS picker.`,
      });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, upsert, refresh]);

  const columns: ReadonlyArray<DataTableColumn<NaicsCodeRow>> = useMemo(
    () => [
      {
        header: 'Code',
        cell: (r) => <code className="cell-mono--bold">{r.code}</code>,
        sortKey: (r) => r.code,
      },
      {
        header: 'Title',
        cell: (r) => <span className="cell-strong">{r.title}</span>,
        sortKey: (r) => r.title,
      },
      {
        header: 'Sector',
        cell: (r) => (
          <span className="cell-desc">
            {r.sectorCode} · {sectorByCode.get(r.sectorCode)?.name ?? `Sector ${r.sectorCode}`}
          </span>
        ),
        sortKey: (r) => r.sectorCode,
      },
      {
        header: 'Depth',
        cell: (r) => <span className="num">{r.depth}</span>,
        sortKey: (r) => r.depth,
      },
      {
        header: 'Parent',
        cell: (r) => <code className="cell-mono">{r.parentCode || '—'}</code>,
      },
      {
        header: 'Status',
        cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft({ ...r, isNew: false }); setSaveError(null); }}>Edit</Button>
            {r.isActive && <Button onClick={() => setDeleteTarget(r)}>Deactivate</Button>}
          </div>
        ),
      },
    ],
    [sectorByCode],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load NAICS codes: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!codes) return <Spinner label="Loading NAICS codes…" />;

  const defaultDraftSector = sectorFilter || sectors[0]?.sectorCode || '';

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="warn">
        Reference data sourced from the U.S. Census Bureau / IRS NAICS catalog. Every Business entity
        in the CIF carries one of these codes. Edit cautiously — renaming a code does not retroactively
        update existing customer records, and the IRS only revises this list every few years.
      </Banner>

      <Card
        title={`NAICS Codes — ${visible.length} of ${codes.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <SelectField
                label=""
                value={sectorFilter}
                onChange={setSectorFilter}
                options={sectorOptions}
              />
            </div>
            <div className="users-filter">
              <TextField
                label=""
                value={search}
                onChange={setSearch}
                placeholder="code or industry title…"
              />
            </div>
            <Button
              variant="primary"
              onClick={() => { setDraft(emptyDraft(defaultDraftSector)); setSaveError(null); }}
            >
              + Add NAICS Code
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          Business entity types read from this catalog so officers can classify the business they're
          opening for. Filter by sector or search by code/title. Click a column header to sort.
        </p>
        <DataTable<NaicsCodeRow>
          columns={columns}
          rows={visible}
          rowKey={(r) => r.code}
          emptyState={<p className="empty">No NAICS codes match the current filters.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add a NAICS code' : `Edit NAICS ${draft?.code ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={680}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="NAICS code"
                value={draft.code}
                onChange={(v) =>
                  draft.isNew &&
                  setDraft({ ...draft, code: v.replace(/\D/g, '').slice(0, 8) })
                }
                disabled={!draft.isNew}
                monospace
                placeholder="e.g. 522110"
                hint={draft.isNew ? '2–8 digits. Permanent — cannot change once saved.' : 'Permanent.'}
              />
              <SelectField
                label="Sector"
                value={draft.sectorCode}
                onChange={(v) => setDraft({ ...draft, sectorCode: v })}
                options={draftSectorOptions}
                hint="2-digit sector this code rolls up into."
              />
            </div>

            <TextField
              label="Title"
              value={draft.title}
              onChange={(v) => setDraft({ ...draft, title: v.slice(0, 256) })}
              placeholder="e.g. Commercial Banking"
              hint="Industry name shown to officers. Match Census wording when possible."
            />

            <div className="form-row">
              <NumberField
                label="Depth"
                value={draft.depth}
                onChange={(v) =>
                  setDraft({ ...draft, depth: v == null ? 6 : Math.max(2, Math.min(8, v)) })
                }
                min={2}
                max={8}
                placeholder="6"
                hint="Number of digits in the code (2–8). Defaults to the code length."
              />
              <TextField
                label="Parent code"
                value={draft.parentCode ?? ''}
                onChange={(v) =>
                  setDraft({ ...draft, parentCode: v.replace(/\D/g, '').slice(0, 8) || null })
                }
                monospace
                placeholder="e.g. 5221"
                hint="The shorter NAICS code this rolls up into. Optional."
              />
            </div>

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive codes hide from the officer's NAICS picker but stay for existing records."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Mark this NAICS code inactive?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleSoftDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Mark inactive'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We&apos;ll mark <code className="cell-mono">{deleteTarget.code}</code> (
              {deleteTarget.title}) inactive. Officers won&apos;t see it as a choice anymore, but the
              row stays for any existing Business records that reference it. Flip it back on anytime.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
